import { parseAddress } from '@featit/shared/addressing/paths';
import { workAreaRouteRef } from '@featit/shared/addressing/workAreaRouteRef';
import { type IProject } from '@featit/shared/types/index';
import { findWorkAreaByRouteRef } from '@/routing/workAreaRoute';
import {
  isNamespaceScopedPath,
  isWorkAreaOverviewPath,
  namespaceActivityPath,
  namespaceIdOfPath,
  WORK_AREAS_HOME_PATH,
} from '@/features/work-areas/workAreaOverviewUrls';
import { projectHomePath } from '@/features/work-items/projectItemUrls';

/**
 * F-0502 (`docs/concept-mobile-app.md`, AC-18.7 and AC-18.8): the native More drawer's panel
 * (`/-/more?panel=nav`) is a web view of its own, kept warm between opens, with its own store. Its
 * scope has to be kept in step from both directions: what the person picks in it, and what the
 * content screen under it shows.
 */
export interface IDrawerPanelScopeStore {
  projectMap: Record<string, IProject>;
  projects: readonly IProject[];
  selectProject(id: string): void;
  enterWorkArea(id: string): void;
  selectAllWorkAreas(options?: { remember?: boolean }): void;
  selectNamespace(namespaceId: string): void;
}

/**
 * AC-18.7: what a scope picked in the panel does. The shell opens it in the content web view
 * (`drawerNavigate`), and the panel takes the same scope itself, or its next open still shows the
 * scope it had (measured on the simulator, 2026-10-03: the pick opened the work area and the panel
 * still read "All work areas").
 */
export function drawerPanelPicks(
  projectStore: IDrawerPanelScopeStore,
  drawerNavigate: (path: string) => void,
) {
  return {
    onProjectSelect(projectId: string): void {
      const project = projectStore.projectMap[projectId];
      if (!project) return;
      projectStore.selectProject(projectId);
      drawerNavigate(projectHomePath(workAreaRouteRef(project)));
    },
    onWorkAreaOverview(): void {
      projectStore.selectAllWorkAreas();
      drawerNavigate(WORK_AREAS_HOME_PATH);
    },
    onSelectNamespace(namespaceId: string): void {
      projectStore.selectNamespace(namespaceId);
      drawerNavigate(namespaceActivityPath(namespaceId));
    },
  };
}

/**
 * AC-18.8: the `scope` event the shell sends as the drawer opens, with the content screen's path.
 * The scope that path names becomes the panel's: a work area, a namespace, or All work areas. A path
 * that names none (`/-/agents`, `/-/settings`) leaves the panel's scope as it is.
 */
export function followContentScope(projectStore: IDrawerPanelScopeStore, path: string): void {
  const pathname = path.split('#', 1)[0].split('?', 1)[0];
  if (isWorkAreaOverviewPath(pathname)) {
    // Following the screen under the drawer is not the person choosing All (I-1118).
    projectStore.selectAllWorkAreas({ remember: false });
    return;
  }
  if (isNamespaceScopedPath(pathname)) {
    const namespaceId = namespaceIdOfPath(pathname);
    if (namespaceId) projectStore.selectNamespace(namespaceId);
    return;
  }
  const address = parseAddress(pathname);
  if (address.kind !== 'work-area') return;
  const workArea = findWorkAreaByRouteRef(projectStore.projects, address.reference);
  if (workArea) projectStore.enterWorkArea(workArea.id);
}
